/* eslint-disable @typescript-eslint/restrict-template-expressions */
import * as Commands from '@commands';
import { type CommandContext } from '@commands';
import { LayoutOrientation } from '@doc';
import { DEFAULT_MIN_ZOOM, MAX_ZOOM } from '@env';
import type { PointObject, Rect, RectLTWH, RectObject } from '@geom';
import { Point, point, rect } from '@geom';
import { clamp } from '@utils';

import { ScriptingAPI } from './ScriptingAPI';

type ScriptPoint = [number, number] | PointObject;
type ScriptRect = [number, number, number, number] | RectObject | RectLTWH;

function assertScriptPoint(p: unknown): asserts p is ScriptPoint {
  if (Array.isArray(p)) {
    if (p.length !== 2 || typeof p[0] !== 'number' || typeof p[1] !== 'number') {
      throw new Error(`Invalid point: ${p}`);
    }
  } else if (p !== null && typeof p === 'object') {
    if (
      !('x' in p) ||
      !('y' in p) ||
      typeof p.x !== 'number' ||
      typeof p.y !== 'number'
    ) {
      throw new Error(`Invalid point: ${JSON.stringify(p)}`);
    }
  } else {
    throw new Error(`Invalid point: ${p}`);
  }
}

function assertScriptRect(r: unknown): asserts r is ScriptRect {
  if (Array.isArray(r)) {
    if (
      r.length !== 4 ||
      typeof r[0] !== 'number' ||
      typeof r[1] !== 'number' ||
      typeof r[2] !== 'number' ||
      typeof r[3] !== 'number'
    ) {
      throw new Error(`Invalid rect: ${r}`);
    }
  } else if (r !== null && typeof r === 'object') {
    if (
      !('x' in r) ||
      !('y' in r) ||
      !('width' in r) ||
      !('height' in r) ||
      typeof r.x !== 'number' ||
      typeof r.y !== 'number' ||
      typeof r.width !== 'number' ||
      typeof r.height !== 'number'
    ) {
      throw new Error(`Invalid rect: ${JSON.stringify(r)}`);
    }
  } else {
    throw new Error(`Invalid rect: ${r}`);
  }
}

function toPoint(p: unknown): Point {
  assertScriptPoint(p);
  return point(p);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function toRect(r: unknown): Rect {
  assertScriptRect(r);
  return rect(r);
}

function toScriptPoint(p: Point): ScriptPoint {
  return p.object;
}

function toScriptRect(r: Rect): ScriptRect {
  return r.ltwh;
}

function viewBounds(context: CommandContext): Rect {
  return elemBoundsToDocBounds(context);
}

function uiToDocPoint(uiPoint: Point, context: CommandContext): Point {
  return uiPoint.div(context.zoom);
}

function docToUiPoint(docPoint: Point, context: CommandContext): Point {
  return docPoint.mul(context.zoom);
}

export function docToElemPoint(docPoint: Point, context: CommandContext): Point {
  const { pan, zoom, elemBounds } = context;
  return elemBounds.topLeft.add(docPoint.mul(zoom)).add(pan);
}

export function elemBoundsToDocBounds(context: CommandContext): Rect {
  const { elemBounds, pan, zoom } = context;
  return elemBounds.aligningTopLeftToOrigin().sub(pan).div(zoom);
}

// This is the scripting API object for a given document. It provides access
// to the document's settings and commands.
export class DocScriptingAPI extends ScriptingAPI {
  //
  // View
  //

  get viewBounds(): ScriptRect {
    const context = this.commandContext;
    return toScriptRect(viewBounds(context));
  }

  get viewCenter(): ScriptPoint {
    const context = this.commandContext;
    return toScriptPoint(viewBounds(context).center);
  }

  set viewCenter(p: ScriptPoint) {
    const context = this.commandContext;

    const currentCenter = viewBounds(context).center;
    console.log('currentCenter', currentCenter.toString());

    const newCenter = toPoint(p);
    console.log('newCenter', newCenter.toString());

    const delta = newCenter.sub(currentCenter);
    console.log('delta', delta.toString());

    const currentPan = context.pan;
    console.log('currentPan', currentPan.toString());

    const newPan = currentPan.sub(delta);
    console.log('newPan', newPan.toString());

    const result = { pan: newPan };
    context.update(result);
    context.commit();
  }

  //
  // Pan
  //

  get pan(): ScriptPoint {
    const context = this.commandContext;
    return toScriptPoint(uiToDocPoint(context.pan, context));
  }

  set pan(p: ScriptPoint) {
    const context = this.commandContext;
    const pan = docToUiPoint(toPoint(p), context);
    const result = { pan };
    context.update(result);
    context.commit();
  }

  //
  // Zoom
  //

  get zoom(): number {
    const context = this.commandContext;
    return context.zoom;
  }

  set zoom(value: number) {
    this.zoomTo(value);
  }

  get minZoom(): number {
    const context = this.commandContext;
    const result = Commands.calculateZoomToFit(context);
    return result?.zoom ?? DEFAULT_MIN_ZOOM;
  }

  get maxZoom(): number {
    return MAX_ZOOM;
  }

  zoomToFit(): void {
    const context = this.commandContext;
    const result = Commands.zoomToFit(context);
    context.update(result);
    context.commit();
  }

  zoomTo(scale: number, position?: ScriptPoint): void {
    const p = position ?? this.pan;
    const s = clamp(scale, [this.minZoom, this.maxZoom]);
    const context = this.commandContext;
    const elemPoint = docToElemPoint(toPoint(p), context);
    const result = Commands.zoom(s, elemPoint, context);
    context.update(result);
    context.commit();
  }

  //
  // Layout Orientation
  //

  get orientations(): LayoutOrientation[] {
    return Object.values(LayoutOrientation);
  }

  get orientation(): LayoutOrientation {
    const context = this.commandContext;
    return context.doc.orientation;
  }

  set orientation(orientation: LayoutOrientation) {
    if (!this.orientations.includes(orientation)) {
      throw new Error(`Invalid orientation: ${orientation}`);
    }
    const context = this.commandContext;
    const result = Commands.setOrientation(orientation, context);
    context.update(result);
    context.commit();
  }

  //
  // Layout Alignment
  //

  get alignStartNodes(): boolean {
    return this.commandContext.doc.alignRoots;
  }

  set alignStartNodes(value: boolean) {
    const context = this.commandContext;
    const result = Commands.setAlignRoots(context, value);
    context.update(result);
    context.commit();
  }

  get alignEndNodes(): boolean {
    return this.commandContext.doc.alignLeaves;
  }

  set alignEndNodes(value: boolean) {
    const context = this.commandContext;
    const result = Commands.setAlignLeaves(context, value);
    context.update(result);
    context.commit();
  }
}
